export type SubscriptionState = 'none' | 'trial' | 'active' | 'past_due' | 'cancelled'

export type CardBrand = 'visa' | 'mastercard' | 'mir' | 'maestro' | 'unionpay' | 'sbp'

export interface SubscriptionCard {
  brand: CardBrand
  lastFour: string
  expiry: string
}

export interface PastDueInfo {
  attemptsRemaining: number | null
  nextRetryDate: string
  outstandingAmount: number
  failureReason?: string
}

export interface Subscription {
  state: SubscriptionState
  plan: 'pro'
  currentAmount: number
  subscriptionStartedAt?: string
  trialEndsAt?: string
  nextChargeDate?: string
  nextChargeAmount?: number
  accessEndsAt?: string
  reactivationDeadline?: string
  hasPaymentMethod?: boolean   // true если есть payment_method_id для рекуррента (даже если карта legacy без метаданных)
  card?: SubscriptionCard
  pastDue?: PastDueInfo
}

export type PaymentType = 'subscription' | 'trial' | 'refund'
export type PaymentStatus = 'paid' | 'failed' | 'refunded'

export interface PaymentMethodCard {
  brand: 'visa' | 'mastercard' | 'mir' | 'maestro' | 'unionpay'
  lastFour: string
}
export interface PaymentMethodOther {
  type: 'sbp' | 'other'
}
export type PaymentMethod = PaymentMethodCard | PaymentMethodOther

export interface PaymentHistoryEntry {
  id: string
  date: string
  type: PaymentType
  description: string
  amount: number
  status: PaymentStatus
  method?: PaymentMethod | null
}

export interface PaymentHistory {
  payments: PaymentHistoryEntry[]
  totalPaid: number
  hasMore: boolean
}

export async function getSubscription(channelUUID: string): Promise<Subscription> {
  const res = await fetch(`/api/max/subscription/${encodeURIComponent(channelUUID)}`, {
    credentials: 'include',
  })
  if (!res.ok) {
    let body: { error?: string } = {}
    try {
      body = await res.json()
    } catch {}
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export async function getPaymentHistory(
  channelUUID: string,
  opts?: { limit?: number; offset?: number },
): Promise<PaymentHistory> {
  const params = new URLSearchParams()
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.offset) params.set('offset', String(opts.offset))
  const qs = params.toString()
  const res = await fetch(
    `/api/max/payment/history/${encodeURIComponent(channelUUID)}${qs ? '?' + qs : ''}`,
    { credentials: 'include' },
  )
  if (!res.ok) {
    let body: { error?: string } = {}
    try {
      body = await res.json()
    } catch {}
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export type CancelReason =
  | 'expensive'
  | 'unclear'
  | 'not_profitable'
  | 'missing_feature'
  | 'just_trying'
  | 'other'

export const CANCEL_REASON_LABELS: Record<CancelReason, string> = {
  expensive: 'Дорого для моего канала',
  unclear: 'Не разобрался, как работает',
  not_profitable: 'Реклама не окупает себя',
  missing_feature: 'Не нашёл нужной фичи',
  just_trying: 'Просто хотел попробовать',
  other: 'Другое',
}

export async function cancelSubscription(
  channelUUID: string,
  reason: CancelReason,
  comment?: string,
): Promise<Subscription> {
  const res = await fetch('/api/max/subscription/cancel', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channelUUID, reason, comment: comment || undefined }),
  })
  if (!res.ok) {
    let body: { error?: string; message?: string } = {}
    try { body = await res.json() } catch {}
    const err = new Error(body.error || body.message || `HTTP ${res.status}`) as Error & { code?: string }
    if (body.error) err.code = body.error
    throw err
  }
  const data = await res.json()
  return data.subscription as Subscription
}

export async function reactivateSubscription(channelUUID: string): Promise<Subscription> {
  const res = await fetch('/api/max/subscription/reactivate', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channelUUID }),
  })
  if (!res.ok) {
    let body: { error?: string; message?: string } = {}
    try { body = await res.json() } catch {}
    const err = new Error(body.error || body.message || `HTTP ${res.status}`) as Error & { code?: string }
    if (body.error) err.code = body.error
    throw err
  }
  const data = await res.json()
  return data.subscription as Subscription
}

export interface ChangeCardResponse {
  success: boolean
  paymentUrl?: string
  error?: string
}

export async function startChangeCard(channelUUID: string, email: string): Promise<ChangeCardResponse> {
  const res = await fetch('/api/max/subscription/change-card', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channelUUID, email, clientType: 'desktop' }),
  })
  if (res.status === 404) {
    const err = new Error('NOT_IMPLEMENTED') as Error & { code?: string }
    err.code = 'NOT_IMPLEMENTED'
    throw err
  }
  if (!res.ok) {
    let body: { error?: string } = {}
    try {
      body = await res.json()
    } catch {}
    const err = new Error(body.error || `HTTP ${res.status}`) as Error & { code?: string }
    if (body.error) err.code = body.error
    throw err
  }
  return res.json()
}

export const CARD_BRAND_LABELS: Record<CardBrand, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  mir: 'МИР',
  maestro: 'Maestro',
  unionpay: 'UnionPay',
  sbp: 'СБП',
}

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  paid: 'Оплачено',
  failed: 'Неудачно',
  refunded: 'Возврат',
}
